import { Trans } from '@lingui/macro'
import {
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Radio,
  RadioGroup
} from '@material-ui/core'
import {
  mapContact,
  saveContactFromLookupField
} from 'app/services/sfAuth/sfData/sfContact'
import FindContactButton from 'app/views/grants/FindContactButton'
import { Field, useFormikContext } from 'formik'
import React from 'react'
import { FormConnectToObject, getMuState, MuEditedByLabel } from '../Common'
import { getMainConnected } from '../../Form'
import { useDispatch, useSelector } from 'react-redux'
import { FormErrorLabel } from '../FormErrorLabel'
import {
  endEditingField,
  startEditingField
} from '../../multiuser/grpcMultiuserEdit'
import { EditContactPanel } from './FormAssociatedContactsList'
import { addOpportunityContactAffiliation } from 'app/services/sfAuth/sfData/sfOpportunityAffiliation'
import {
  addAccountContactAffiliation,
  setAcountAffiliationToFormer
} from 'app/services/sfAuth/sfData/sfAccount'
import moment from 'moment'
import { dateFormat } from 'app/appSettings'
import { TooltipLabelIcon } from 'app/views/page-layouts/TooltipLabelIcon'
import { useEffect } from 'react'

export const formContactPickerExtractSaveKey = ({
  saveMap,
  item,
  sfObject,
  value,
  connectedProps,
  subObjectsMap,
  connectedObjectId
}) => {
  const { connectedField, connectedObject } = connectedProps
  const fieldName = connectedField?.name
  if (fieldName && connectedObject) {
    saveMap[connectedObject][fieldName] = value?.id
  }
}

export const formContactPickerSavePromise = ({
  value,
  item,
  connectedObject,
  additionalSFInfo
}) => {
  const objectName = connectedObject.attributes.type
  const objectId = connectedObject.Id
  const { affiliationRole } = item.typeProps
  const { connectedField } = getMainConnected(item)
  const fieldName = connectedField?.name
  if (!value || !fieldName) {
    return null
  }
  const { id, affiliation } = value
  const fieldValue = value.id
  if (affiliationRole && !affiliation) {
    return addAccountContactAffiliation({
      role: affiliationRole,
      contactId: fieldValue,
      accountId: objectId
    })
  } else if (
    affiliationRole &&
    affiliation &&
    affiliation.npe5__Contact__c.Id !== fieldValue
  ) {
    return Promise.all([
      addAccountContactAffiliation({
        role: affiliationRole,
        contactId: fieldValue,
        accountId: objectId
      }),
      setAcountAffiliationToFormer(affiliation.Id)
    ])
  }
}

export const formContactParseValueToCompare = (value, props) => {
  if (props.saveFailed && value) {
    let toRet = value.firstName + ' ' + value.lastName
    if (value.email) {
      toRet += ', ' + value.email
    }
    return toRet
  }
  if (value && value.id) {
    return value.id
  } else {
    return value
  }
}

export const formContactPickerValueToText = (
  value,
  question,
  { contactsMap }
) => {
  if (typeof value === 'string') {
    const contact = contactsMap[value] || {}
    let toRet = contact.FirstName + ' ' + contact.LastName
    if (contact.Email) {
      toRet += ', ' + contact.Email
    }
    return {
      en: toRet,
      fr: toRet
    }
  } else if (!value) {
    return {
      en: '',
      fr: ''
    }
  }
  let toRet = value.firstName + ' ' + value.lastName
  if (value.email) {
    toRet += ', ' + value.email
  }
  if (value.jobTitle) {
    toRet += ', ' + value.jobTitle
  }
  return {
    en: toRet,
    fr: toRet
  }
}

export const formContactPickerDefaultValue = (obj, additionalInfo, item) => {
  const { contactsMap = {}, accountAffiliations = [] } = additionalInfo
  if (!obj) {
    return null
  } else {
    const { connectedField } = getMainConnected(item)
    const { affiliationRole } = item.typeProps
    const field = connectedField?.name
    if (obj[field] && contactsMap[obj[field]]) {
      const contact = contactsMap[obj[field]]
      let relatedAffiliation
      if (affiliationRole && accountAffiliations) {
        accountAffiliations.some(affiliation => {
          if (
            affiliation.npe5__Role__c === affiliationRole &&
            affiliation.npe5__Status__c === 'Current'
          ) {
            relatedAffiliation = affiliation
            return true
          } else {
            return false
          }
        })
      }
      return { ...mapContact(contact), affiliation: relatedAffiliation }
    } else {
      return obj[field]
    }
  }
}

export const FormContactPickerPrint = ({ value, title, typeProps }) => {
  const { printLabelPlacement } = typeProps
  const top = !printLabelPlacement || printLabelPlacement === 'top'
  let valueToShow = value ? value.firstName + ' ' + value.lastName : ''
  if (valueToShow && value.jobTitle) {
    valueToShow += ', ' + value.jobTitle
  }
  if (valueToShow && value.email) {
    valueToShow += ', ' + value.email
  }
  return (
    <div>
      {top && <Typography className='form-print-subtitle'>{title}</Typography>}
      <Typography style={{ whiteSpace: 'pre-line' }}>{valueToShow}</Typography>
      {!top && <Typography className='form-print-subtitle'>{title}</Typography>}
    </div>
  )
}

export const FormContactPicker = ({
  id,
  editMode,
  disabled,
  saveDisabled,
  useMultiuser,
  formId,
  connectedObject,
  reloadLastModifiedDates,
  typeProps
}) => {
  const user = useSelector(state => state.user)
  const { values, setFieldValue } = useFormikContext()
  const { isEdited, userColor, userName } = getMuState({ values, id })
  const {
    connectedTo = [],
    defaultOrganization,
    workEmailInContactCreation
  } = typeProps

  return (
    <Field name={id}>
      {({ field: { value }, meta }) => {
        const onContactFound = ({ ...contact }) => {
          const newValue = { ...value, ...mapContact(contact) }
          setFieldValue(id, newValue)
          if (useMultiuser) {
            endEditingField({
              userId: user.userId,
              formId,
              fieldId: id,
              fieldValue: newValue,
              lockId: values.muInfo.lockId
            })
          }
        }

        const onContactUpdate = contact => {
          setFieldValue(id, { ...value, ...contact })
          if (useMultiuser) {
            endEditingField({
              userId: user.userId,
              formId,
              fieldId: id,
              fieldValue: { ...value, ...contact },
              lockId: values.muInfo.lockId
            })
          }
        }

        return (
          <>
            <Card
              elevation={5}
              style={{
                padding: 20,
                border: '1px solid #C4C4C4'
              }}
            >
              <Grid container direction='column' justify='center'>
                <div style={{ marginBottom: 10 }}>
                  <FindContactButton
                    isContactFound={Boolean(value && typeof value === 'object')}
                    preview={saveDisabled}
                    onDialogClose={e => {
                      if (useMultiuser) {
                        endEditingField({
                          userId: user.userId,
                          formId,
                          fieldId: id,
                          fieldValue: value,
                          lockId: values.muInfo.lockId
                        })
                      }
                    }}
                    onDialogOpen={e => {
                      if (useMultiuser) {
                        startEditingField({
                          userId: user.userId,
                          formId,
                          fieldId: id
                        })
                      }
                    }}
                    useWorkEmailInNewContact={Boolean(
                      workEmailInContactCreation
                    )}
                    allowCreatingNewContact
                    disabled={
                      disabled ||
                      editMode ||
                      connectedTo.length === 0 ||
                      Boolean(isEdited && useMultiuser)
                    }
                    onSelect={onContactFound}
                    currentOrganizationAsDefault={
                      defaultOrganization === 'current'
                    }
                  />
                </div>

                {Boolean(value && typeof value === 'object') && (
                  <Grid container>
                    <Grid item>
                      <b>
                        <span>{value.firstName + ' ' + value.lastName}</span>{' '}
                      </b>
                      <div>
                        <span>{value.email}</span>
                      </div>
                    </Grid>
                    <EditContactPanel
                      disabled={disabled || saveDisabled}
                      onContactUpdate={onContactUpdate}
                      contactInfo={value}
                      reloadLastModifiedDates={reloadLastModifiedDates}
                      renderIconOnLeft
                      // programManager={connectedObject.Assigned_Program_Manager__c}
                    />
                  </Grid>
                )}
              </Grid>
            </Card>
            <FormErrorLabel id={id} error={meta.error} />
            {isEdited && (
              <div style={{ marginTop: 5 }}>
                <MuEditedByLabel color={userColor} userName={userName} />
              </div>
            )}
          </>
        )
      }}
    </Field>
  )
}

export const FormEditorContactPicker = ({
  editMode,
  showPrintProps,
  depth,
  typeProps,
  ...props
}) => {
  const {
    required,
    affiliationRole,
    defaultOrganization,
    workEmailInContactCreation
  } = typeProps
  const dispatch = useDispatch()
  const { values } = useFormikContext()
  const { connectedField, connectedObject } = getMainConnected({
    typeProps,
    ...props
  })

  let connectedToAccount
  if (
    connectedObject &&
    values.objectsConnected.some(
      obj => obj.identId === connectedObject && obj.type === 'Account'
    )
  ) {
    connectedToAccount = true
  }

  useEffect(() => {
    if (!connectedToAccount && affiliationRole) {
      const toSet = { ...typeProps }
      delete toSet.affiliationRole
      dispatch({
        type: 'FIELD',
        depth: depth.split('.'),
        fieldName: 'typeProps',
        fieldValue: toSet
      })
    }
  }, [connectedToAccount])

  if (!editMode) {
    return <FormContactPicker editMode typeProps={typeProps} {...props} />
  }

  return (
    <div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(required)}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.required = e.target.checked
                dispatch({
                  type: 'FIELD',
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: { ...toSet }
                })
              }}
            />
          }
          label={<Trans>Required</Trans>}
        />
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(workEmailInContactCreation)}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.workEmailInContactCreation = e.target.checked
                dispatch({
                  type: 'FIELD',
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: { ...toSet }
                })
              }}
            />
          }
          label={<Trans>Use work email in contact creation</Trans>}
        />
      </div>
      <TextField
        variant='outlined'
        label={<Trans>Default organization</Trans>}
        fullWidth
        select
        value={defaultOrganization || ''}
        style={{ marginTop: 5 }}
        onChange={e => {
          const toSet = { ...typeProps }
          toSet.defaultOrganization = e.target.value
          dispatch({
            type: 'FIELD',
            depth: depth.split('.'),
            fieldName: 'typeProps',
            fieldValue: toSet
          })
        }}
      >
        <MenuItem value='current' key='current'>
          <Trans>Current Organization</Trans>
        </MenuItem>
        <MenuItem value='household' key='household'>
          <Trans>Household</Trans>
        </MenuItem>
      </TextField>
      {connectedToAccount && (
        <Grid container>
          <Grid item xs style={{ paddingRight: 5 }}>
            <TextField
              variant='outlined'
              label={<Trans>Account affiliation role</Trans>}
              fullWidth
              value={affiliationRole || ''}
              style={{ marginTop: 10 }}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.affiliationRole = e.target.value
                dispatch({
                  type: 'FIELD',
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: toSet
                })
              }}
            />
          </Grid>
          <TooltipLabelIcon
            tooltip={
              <Trans>
                If account affiliation role is not empty, the affiliation object
                will be created and updated when contact is picked
              </Trans>
            }
          />
        </Grid>
      )}
      <FormConnectToObject
        typeProps={typeProps}
        depth={depth}
        filter={item =>
          item.type === 'reference' && item.referenceTo.includes('Contact')
        }
      />
    </div>
  )
}
